<template>
  <PageLoader v-if="isLoading" />
  <div
    v-else
    class="pets"
  >
    <header
      class="pets__header"
      :style="`padding-top: ${SafeAreaView.top + 12}px`"
    >
      <button
        type="button"
        class="pets__back"
        @click="goBack"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        ><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
      </button>
      <label class="pets__search">
        <input
          v-model="search"
          type="text"
          :placeholder="t('animals.search')"
        >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
        >
          <circle
            cx="11"
            cy="11"
            r="7"
          />
          <path d="M20 20l-3.5-3.5" />
        </svg>
      </label>
    </header>

    <div class="pets__content">
      <div
        v-if="animals.length === 0"
        class="info"
      >
        {{ t('animals.null') }}
      </div>

      <div class="pet-list">
        <router-link
          v-for="item in filtered"
          :key="item.id"
          data-test-id="petCreate"
          :to="'/animal/' + item.id"
          class="pet"
        >
          <img
            v-if="photoOf(item)"
            :src="photoOf(item)"
            class="pet__photo"
            alt=""
          >
          <IconBase
            v-else
            :path="`animals/${getAnimalById(item?.payload?.animal_id || item?.animal_id).icon}.svg`"
            class="pet__photo pet__photo--icon"
          />
          <div class="body">
            <div class="title">
              {{ item.payload?.name }}
            </div>
            <div class="subtitle">
              {{ item.payload?.breed }}
            </div>
          </div>
          <img
            src="@/assets/icons/other/context.svg"
            class="context"
            @click.prevent="openContextAnimal(item.id)"
          >
          <span class="pet__arrow">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </span>
        </router-link>
      </div>

      <HomePlaces />
      <HomeProducts />
    </div>

    <AddEntity to="/pet-create" />
  </div>

  <ContextPane
    ref="ContextPaneREF"
    :actions="['edit', 'delete']"
    @on-delete="onDelete"
    @on-edit="onEdit"
  />
</template>

<script setup>
import { IconBase } from '@/UI'
import { ContextPane } from '@/components/ContextPane'
import { computed, onMounted, ref } from 'vue';
import { SafeAreaView } from '@/helpers/getSafeArea'
import HomePlaces from '@/screens/MenuView/components/HomePlaces.vue'
import HomeProducts from '@/screens/MenuView/components/HomeProducts.vue'
import { useNavigation } from '@/composables/useNavigation'
import { getAnimals } from './api'
import { PageLoader } from '@/components/PageLoader'
import { deleteAnimal } from './api';
import { successToast, errorToast } from '@/helpers/showToast'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useConfirm } from '@/composables/useConfirm'
import AddEntity from '@/components/AddEntity.vue';
import { getAnimalById } from '@/helpers/helpers';

const { t } = useI18n()
const { goBack } = useNavigation()

const router = useRouter()

const isLoading = ref(true)
const animals = ref([])
const ContextPaneREF = ref()
const search = ref('')

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) {return animals.value}
  return animals.value.filter((el) => `${el.payload?.name || ''} ${el.payload?.breed || ''}`.toLowerCase().includes(q))
})

const photoOf = (pet) => pet.payload?.photos?.[0]?.path || pet.payload?.photo || ''

const init = async () => {
  animals.value = (await getAnimals())?.animals || []
  isLoading.value = false

  // единственный питомец — сразу открываем его страницу
  if (animals.value.length === 1) {
    router.replace(`/animal/${animals.value[0].id}`)
  }
}

const openContextAnimal = (id) => {
  ContextPaneREF.value.open({ id })
}

const onEdit = (id) => {
  router.push(`/animal-edit/${ id}`)
}

const { onConfirm } = useConfirm();
const onDelete = async (id) => {
  ContextPaneREF.value.close();
  const response = await onConfirm({ title: t('animal.toast.removeConfirm') })

  if (response) {
    const { success } = await deleteAnimal({ id })
    if (success) {
      successToast({ description: t('animal.toast.removeSuccess') })

    } else {
      errorToast({ description: t('toast.somethingWrong') })
    }

    animals.value = animals.value.filter(el => el.id !== id)
  }
}

onMounted(() => {
  init()
})
</script>

<style lang="scss" scoped>
.pets {
  min-height: 100vh;
  background: #f3f4f8;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px 20px;
    background: #9ad03b;
    border-radius: 0 0 32px 32px;

  &__back {
    flex: none;
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 50%;
    background: #fff;
    color: #26384b;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  }

  &__search {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 48px;
    padding: 0 20px;
    border-radius: 24px;
    background: #fff;
    color: #55627a;

    input {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      font-size: 15px;
      font-family: "NunitoRegular";
      color: #26384b;
    }
  }

  &__content {
    display: grid;
    gap: 24px;
    padding: 20px 20px 110px;
  }
}

.pet-list {
  display: grid;
  gap: 12px;
}

.pet {
  position: relative;
  display: grid;
  grid-template-columns: 140px 1fr;
  min-height: 140px;
  border-radius: 24px;
  background: #fff;
  overflow: hidden;
  color: inherit;
  text-decoration: none;

  &__photo {
    width: 140px;
    height: 140px;
    object-fit: cover;
    border-radius: 24px;

    &--icon {
      padding: 36px;
      background: #f2f8ff;
    }
  }

  &__arrow {
    position: absolute;
    right: 14px;
    bottom: 14px;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #9ad03b;
    color: #26384b;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.body {
  display: grid;
  align-content: start;
  gap: 6px;
  padding: 14px 40px 0 14px;
  min-width: 0;
}

.title {
  color: #26384b;
  font-size: 17px;
  font-family: "NunitoBold";
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subtitle {
  color: #8f98ab;
  font-size: 14px;
  font-family: "NunitoBold";
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.context {
  position: absolute;
  top: 10px;
  right: 6px;
  padding: 8px;
}

.info {
  color: #c6c8da;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

[theme="dark"] {
  .pets__back {
    background: #37363c;
    color: #fff;
  }

  .pets {
    background: #1b1b1b;
  }

  .pet {
    background: #37363c;
  }

  .pet__photo--icon {
    background: #292929;
  }

  .title {
    color: #fff;
  }

  .subtitle {
    color: #9a9eb9;
  }

  .pets__search {
    background: #37363c;

    input {
      color: #fff;
    }
  }
}
</style>
